import { Chip, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { ApiEnumName } from "@/common/services/enum";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import {
  OnboardingDefinitionAdminDto,
  OnboardingDefinitionGetPaginatedAdminDto,
} from "@/core/api/generated/v1";

import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";

export interface OnboardingDefinitionAutocompleteOption extends BaseAutocompleteOption<OnboardingDefinitionAdminDto> {
  subtitle?: string;
}

export const getOnboardingDefinitionOptionTitle = (data: OnboardingDefinitionAdminDto): string =>
  StringHelper.join([data.globalNumber, data.name || data.title], ", ", {
    skipEmpty: true,
  });

export const onboardingDefinitionToOption = (
  data: OnboardingDefinitionAdminDto,
): OnboardingDefinitionAutocompleteOption => ({
  id: data.id!,
  title: getOnboardingDefinitionOptionTitle(data),
  subtitle: undefined,
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface AdminOnboardingDefinitionAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<OnboardingDefinitionAdminDto> {
  searchFilters?: Partial<Omit<OnboardingDefinitionGetPaginatedAdminDto, "search" | "includeIds">>;
}

export function AdminOnboardingDefinitionAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: AdminOnboardingDefinitionAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={onboardingDefinitionToOption}
      isPreload={isPreload}
      request={{
        requestFunc:
          apiClient.onboardingDefinitionsAdminApi.apiV1AdminGuidanceOnboardingDefinitionsGetPost,
        limit: 25,
        parameters: {
          onboardingDefinitionGetPaginatedAdminDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          onboardingDefinitionGetPaginatedAdminDto: {
            ...params.onboardingDefinitionGetPaginatedAdminDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Onboarding definition'
      placeholder='Search...'
      renderOption={(props, option: OnboardingDefinitionAutocompleteOption) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='data' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>

                  {option.subtitle && (
                    <Typography component='div' variant='body1'>
                      {option.subtitle}
                    </Typography>
                  )}

                  {option.data && (
                    <Typography component='div' variant='body1'>
                      {option.data && (
                        <Chip
                          size='small'
                          color='default'
                          variant={"outlined"}
                          label={
                            <InlineApiEnumValue
                              type={ApiEnumName.OnboardingBuiltInType}
                              value={option.data.builtInType}
                            />
                          }
                          sx={{ mr: 1 }}
                        />
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
